import React, { useCallback, useEffect, useState } from 'react';
import MainLayout from '../../layouts/MainLayout';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  message,
  Modal,
  Form,
  Input,
  Popconfirm,
} from 'antd';
import {
  StopOutlined,
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloudSyncOutlined,
} from '@ant-design/icons';
import { callCenterBlacklistApi, show422Error, ValidationError } from '../../utils/api';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import PermissionGate from '../../components/PermissionGate';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';

const CallCenterBlacklist = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1,
  });
  const [addOpen, setAddOpen] = useState(false);
  const [addForm] = Form.useForm();
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);

  const fetchRows = useCallback(async (page = 1, perPage = 20) => {
    setLoading(true);
    try {
      const res = await callCenterBlacklistApi.getAll({ page, per_page: perPage });
      const payload = res?.data ?? {};
      setRows(payload.data ?? []);
      const total = payload.total ?? 0;
      const per = payload.per_page ?? perPage;
      setPagination({
        current_page: payload.current_page ?? page,
        per_page: per,
        total,
        last_page: Math.max(1, Math.ceil(total / per)),
      });
    } catch (error) {
      console.error('خطا در دریافت لیست سیاه:', error);
      message.error(error?.message || 'خطا در دریافت لیست سیاه');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows(pagination.current_page, pagination.per_page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current_page, pagination.per_page]);

  const openAddModal = () => {
    setAddOpen(true);
    addForm.resetFields();
  };

  const submitAdd = async () => {
    try {
      const values = await addForm.validateFields();
      setAddSubmitting(true);
      await callCenterBlacklistApi.add({
        phone: values.phone,
        reason: values.reason,
      });
      message.success('شماره به لیست سیاه اضافه شد');
      setAddOpen(false);
      setPagination((prev) => ({ ...prev, current_page: 1 }));
      await fetchRows(1, pagination.per_page);
    } catch (error) {
      if (error?.errorFields) return;
      console.error('خطا در افزودن به لیست سیاه:', error);
      if (error instanceof ValidationError) {
        show422Error(error, addForm);
      } else {
        message.error(error?.message || 'خطا در افزودن به لیست سیاه');
      }
    } finally {
      setAddSubmitting(false);
    }
  };

  const deleteRow = async (row) => {
    setDeleteLoadingId(row.id);
    try {
      await callCenterBlacklistApi.remove(row.id);
      message.success('شماره از لیست سیاه حذف شد');
      const nextPage =
        rows.length === 1 && pagination.current_page > 1
          ? pagination.current_page - 1
          : pagination.current_page;
      setPagination((prev) => ({ ...prev, current_page: nextPage }));
      await fetchRows(nextPage, pagination.per_page);
    } catch (error) {
      console.error('خطا در حذف از لیست سیاه:', error);
      message.error(error?.message || 'خطا در حذف از لیست سیاه');
    } finally {
      setDeleteLoadingId(null);
    }
  };

  const columns = [
    {
      title: 'شماره تماس',
      dataIndex: 'phone_number',
      key: 'phone_number',
      render: (phone) => (
        <Space>
          <PhoneOutlined className="text-gray-400" />
          <span className="custom-font font-medium" dir="ltr">
            {phone || '—'}
          </span>
        </Space>
      ),
    },
    {
      title: 'دلیل',
      dataIndex: 'reason',
      key: 'reason',
      render: (reason) => (
        <span className="custom-font text-gray-700 dark:text-gray-300">
          {reason || '—'}
        </span>
      ),
    },
    {
      title: 'همگام‌سازی با سانترال',
      key: 'sync_status',
      width: 170,
      render: (_, row) => {
        const synced = row.synced_to_asterisk ?? row.synced_to_simotel ?? row.synced_to_pbx ?? row.is_synced;
        return synced ? (
          <Tag
            icon={<CheckCircleOutlined />}
            color="success"
            className="custom-font"
          >
            همگام شده (AstDB)
          </Tag>
        ) : (
          <Tag
            icon={<CloseCircleOutlined />}
            color="warning"
            className="custom-font"
          >
            در انتظار همگام‌سازی
          </Tag>
        );
      },
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 100,
      render: (_, row) => (
        <PermissionGate
          permission="manage_callcenter_setting"
          fallback={<span className="text-gray-400 text-sm">—</span>}
        >
          <Popconfirm
            title="حذف از لیست سیاه؟"
            description="این شماره دیگر مسدود نخواهد بود."
            okText="بله"
            cancelText="خیر"
            onConfirm={() => deleteRow(row)}
          >
            <Button
              type="link"
              danger
              size="small"
              icon={<DeleteOutlined />}
              loading={deleteLoadingId === row.id}
            >
              حذف
            </Button>
          </Popconfirm>
        </PermissionGate>
      ),
    },
  ];

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rose-500 to-red-600 text-white flex items-center justify-center shadow-lg">
                <StopOutlined className="text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">
                  لیست سیاه
                </h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">
                  مسدودسازی شماره‌های مزاحم در سیستم تلفنی
                </p>
              </div>
            </div>
            <Space wrap>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => fetchRows(pagination.current_page, pagination.per_page)}
                loading={loading}
              >
                بروزرسانی
              </Button>
              <PermissionGate permission="manage_callcenter_setting">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={openAddModal}
                  className={PANEL_PRIMARY_BTN_CLASS}
                >
                  افزودن شماره
                </Button>
              </PermissionGate>
            </Space>
          </div>

          <div className="px-6 py-3 bg-amber-50/80 dark:bg-amber-950/20 border-b border-amber-100 dark:border-amber-900/30 flex items-center gap-2 text-sm text-amber-800 dark:text-amber-200 custom-font">
            <CloudSyncOutlined />
            <span>شماره‌های اضافه‌شده پس از همگام‌سازی، در دیتابیس استریسک (AstDB) نیز مسدود می‌شوند.</span>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            {loading && rows.length === 0 ? (
              <TableSkeleton rows={5} columns={4} />
            ) : (
              <Table
                rowKey="id"
                columns={columns}
                dataSource={rows}
                pagination={{
                  current: pagination.current_page,
                  pageSize: pagination.per_page,
                  total: pagination.total,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50'],
                  showTotal: (total) => (
                    <span className="custom-font">{total} شماره</span>
                  ),
                  onChange: (page, pageSize) => {
                    setPagination((prev) => ({
                      ...prev,
                      current_page: page,
                      per_page: pageSize,
                    }));
                  },
                }}
                locale={{ emptyText: 'شماره‌ای در لیست سیاه ثبت نشده است' }}
              />
            )}
          </div>
        </Card>

        <Modal
          title="افزودن به لیست سیاه"
          open={addOpen}
          onCancel={() => setAddOpen(false)}
          onOk={submitAdd}
          confirmLoading={addSubmitting}
          destroyOnClose
          okText="ثبت"
          cancelText="انصراف"
          width={440}
          okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
        >
          <Form form={addForm} layout="vertical" className="custom-font" style={{ marginTop: 8 }}>
            <Form.Item
              name="phone"
              label="شماره تماس"
              rules={[
                { required: true, message: 'شماره تماس الزامی است' },
                { pattern: /^09\d{9}$/, message: 'فرمت شماره معتبر نیست (مثال: 09121234567)' },
              ]}
            >
              <Input
                placeholder="09120000000"
                className="custom-font"
                dir="ltr"
                prefix={<PhoneOutlined className="text-gray-400" />}
              />
            </Form.Item>
            <Form.Item
              name="reason"
              label="دلیل"
              rules={[{ required: true, message: 'دلیل را وارد کنید' }]}
            >
              <Input.TextArea
                placeholder="مثال: تماس مزاحم، تبلیغاتی، ..."
                rows={3}
                className="custom-font"
                maxLength={255}
                showCount
              />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default CallCenterBlacklist;
